import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { clearSelectedBpRoutine, fetchUserBpsRoutine } from 'features/bpList/ducks/actions'
import { getBpsCount, getPagesCount, getUserBps } from 'features/bpList/ducks/selectors'
import styled from 'styled-components'
import PageHeader from 'components/atoms/PageHeader'
import Table from 'features/bpList/Components/Table'
import { useTranslation } from 'react-i18next'
import Pagination from 'components/atoms/Pagination'
import Filters from 'features/filters/Filters'
import { getFiltersQuery } from 'utils/filters'
import { isNotNilOrEmpty } from 'utils/ramda'
import { not, values, equals, isEmpty } from 'ramda'
import Drawer from '@mui/material/Drawer'
import FilterListIcon from '@mui/icons-material/FilterList'
import EmptyBpList from 'features/bpList/Components/EmptyBpsListState'
import { selectIsFetchingBps } from 'ducks/loaderSelectors'
import CircularProgress from '@mui/material/CircularProgress'
import { clearBpFilesRoutine } from 'ducks/files/actions'
import useMediaQuery from '@mui/material/useMediaQuery'
import useBodyScrollLock from 'hooks/useBodyScrollLock'

const defaultFilters = {
  status: 'draft',
  owner: [],
  responsible_person: [],
  created_at: null,
  updated_at: null,
  building_types: []
}

const Drafts = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const bps = useSelector(getUserBps)
  const pagesCount = useSelector(getPagesCount)
  const totalFound = useSelector(getBpsCount)
  const isLoading = useSelector(selectIsFetchingBps)
  const isMobile = useMediaQuery('(max-width: 1024px)')
  const [isDrawerOpen, setDrawerOpen] = useState(false)

  useBodyScrollLock(isDrawerOpen && isMobile)

  const [filters, setFilters] = useState(defaultFilters)
  const [sortingOptions, setSortingOptions] = useState({
    column: 'name',
    desc: false,
    page: 1
  })

  const handleFetchBps = useCallback(() => {
    const { page, desc, column } = sortingOptions

    dispatch(fetchUserBpsRoutine({
      sort: desc ? `-${column}` : column,
      filters: getFiltersQuery(filters),
      page
    }))
  }, [dispatch, sortingOptions, filters])

  useEffect(() => {
    handleFetchBps()
  }, [handleFetchBps])

  useEffect(() => {
    setSortingOptions(prev => ({
      ...prev,
      page: prev.page > pagesCount ? pagesCount : prev.page
    }))
  }, [pagesCount])

  const handleChangeSorting = column => () => {
    setSortingOptions(prev => ({
      ...prev,
      column,
      desc: column === prev.column ? !prev.desc : false,
      page: 1
    }))
  }

  const handlePageChange = page => {
    setSortingOptions(prev => ({
      ...prev,
      page
    }))
  }

  const openDrawer = () => setDrawerOpen(true)
  const closeDrawer = () => setDrawerOpen(false)

  const handleClearFilters = () => {
    setFilters(defaultFilters)
    setSortingOptions(prev => ({ ...prev, page: 1 }))
  }

  const shouldRenderClearButton = useMemo(() => {
    return values(filters).some(e => isNotNilOrEmpty(e)) && not(equals(filters, defaultFilters))
  }, [filters])

  useEffect(() => {
    dispatch(clearSelectedBpRoutine())
    dispatch(clearBpFilesRoutine())
  }, [dispatch])

  return (
    <PageWrapper>
      <ContentWrapper>
        <HeaderWrapper>
          <PageHeader>
            Drafts
          </PageHeader>
          <FiltersIcon onClick={openDrawer} />
        </HeaderWrapper>

        <ResultInfo>
          <BpsCount>
            {totalFound > 0 && !isLoading && (
              <>
                <span>{totalFound}&nbsp;</span>
                {t('bpList.countResults')}
              </>
            )}
          </BpsCount>

          {shouldRenderClearButton && (
            <ClearFiltersButton onClick={handleClearFilters}>
              {t('bpList.clearFilters')}
            </ClearFiltersButton>
          )}
        </ResultInfo>

        {isLoading ? (
          <Loading><CircularProgress /></Loading>
        ) : isEmpty(bps) && equals(defaultFilters, filters) ? (
          <EmptyBpList />
        ) : (
          <ResponsiveTableWrapper>
            <Table
              handleRefetch={handleFetchBps}
              sortingOptions={sortingOptions}
              sortingChange={handleChangeSorting}
              bps={bps}
              isDraftTable
            />
          </ResponsiveTableWrapper>
        )}

        <Pagination
          onChange={handlePageChange}
          totalPages={pagesCount}
          currentPage={sortingOptions.page}
        />
      </ContentWrapper>

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        variant={isMobile ? 'temporary' : 'persistent'}
        open={isDrawerOpen}
        onClose={closeDrawer}
        style={{ zIndex: 5001 }}

        disableScrollLock={isMobile}

        PaperProps={{
          sx: {
            boxSizing: 'border-box',

            ...(isMobile && {
              width: '100%',
              maxWidth: '100vw',
              height: 'auto',
              maxHeight: '85vh',
              borderTopLeftRadius: '24px',
              borderTopRightRadius: '24px',
              overflowX: 'hidden',
              margin: 0,
            }),

            ...(!isMobile && {
              width: '280px',
              border: 'none',
            })
          }
        }}
      >
        <FiltersWrapper>
          <MobileDrawerHandle />

          <Filters
            closeDrawer={closeDrawer}
            parentFilters={filters}
            getFilters={setFilters}
            defaultFilters={defaultFilters}
            withCloseIcon={!isMobile}
          />
        </FiltersWrapper>
      </Drawer>
    </PageWrapper>
  )
}

export default Drafts

const PageWrapper = styled.div`
  display: flex;
  padding-bottom: 50px;
`

const ContentWrapper = styled.div`
  width: 100%;
`

const FiltersWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  box-sizing: border-box;

  @media (min-width: 1025px) {
    height: 100%;
    padding: 25px 20px;
    overflow-y: auto;
  }

  @media (max-width: 1024px) {
    width: 100%;
    background-color: #fff;
    padding: 0 20px 30px 20px;
    height: auto;
  }
`

const ResultInfo = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`

const BpsCount = styled.div`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 13px;

  span {
    font-weight: bold;
  }
`

const ClearFiltersButton = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  text-transform: uppercase;
  cursor: pointer;
`

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const FiltersIcon = styled(FilterListIcon)`
  transition: all .3s;
  cursor: pointer;
  font-size: 30px !important;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 200px;
`

const ResponsiveTableWrapper = styled.div`
  width: 100%;

  @media (max-width: 1024px) {
    table {
      display: block;
      width: 100%;
      border: none !important;
    }

    thead {
      display: none;
    }

    tbody {
      display: block;
      width: 100%;
    }

    tr {
      display: flex;
      flex-direction: column;
      position: relative;
      background: #ffffff;
      border-radius: 16px;
      margin-bottom: 20px;
      padding: 16px;
      border: none !important;
    }

    td {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 6px 0;
      border: none !important;
      height: 30px !important;
      font-size: 14px;
      color: ${({ theme }) => theme.colors.text || '#333'};
    }

    td::before {
      content: attr(data-label);
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #9aa0ac;
      margin-right: 12px;
      flex-shrink: 0;
      padding-top: 3px;
    }

    /* VALUE STYLING */
    td > * {
      text-align: right;
      width: 100%;
      word-break: break-word;
    }

    td:nth-of-type(1) {
      display: block;
      padding-bottom: 12px;
      margin-bottom: 12px;
      padding-right: 30px;
    }

    td:nth-of-type(1)::before {
      display: none;
    }

    td:nth-of-type(1) > * {
      text-align: left;
      font-size: 16px;
      font-weight: 800;
      color: ${({ theme }) => theme.colors.secondary || '#1a1a1a'};
      line-height: 1.4;
    }

    td:nth-of-type(10) {
      position: absolute;
      top: 14px;
      right: 14px;
      width: auto;
      padding: 0;
    }
    td:nth-of-type(10)::before {
      display: none;
    }

    td:nth-of-type(2)::before { content: 'UPRN'; }
    td:nth-of-type(3)::before { content: 'Address'; }
    td:nth-of-type(4)::before { content: 'Postcode'; }
    td:nth-of-type(5)::before { content: 'Admin'; }

    td:nth-of-type(6) { color: #666; }
    td:nth-of-type(6)::before { content: 'Created'; }

    td:nth-of-type(7) { color: #666; }
    td:nth-of-type(7)::before { content: 'Updated'; }

    td:nth-of-type(8) { font-weight: 600; color: ${({ theme }) => theme.colors.primary}; }
    td:nth-of-type(8)::before { content: 'Price'; }

    td:nth-of-type(9) {
      display: block;
      margin-top: 12px;
      padding-top: 12px;
    }
    td:nth-of-type(9)::before {
      display: none;
    }
    td:nth-of-type(9) > * {
      width: 100%;
      display: block;
      text-align: center;
    }
  }
`

const MobileDrawerHandle = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: block;
    width: 40px;
    height: 4px;
    background-color: #e0e0e0;
    border-radius: 2px;
    margin: 12px auto 20px auto;
  }
`
